import { useCallback, useState } from 'react';
import { clsx } from 'clsx';
import { Upload, Music, X } from 'lucide-react';

interface DropZoneProps {
  onFileSelect: (file: File) => void;
  accept?: string;
  maxSize?: number;
  disabled?: boolean;
  className?: string;
}

const ACCEPTED_AUDIO_TYPES = [
  'audio/mpeg',
  'audio/wav',
  'audio/mp4',
  'audio/x-m4a',
  'audio/ogg',
  'audio/flac',
];

const ACCEPTED_EXTENSIONS = ['.mp3', '.wav', '.m4a', '.ogg', '.flac'];

export default function DropZone({
  onFileSelect,
  accept = ACCEPTED_AUDIO_TYPES.join(','),
  maxSize = 100 * 1024 * 1024, // 100MB default
  disabled = false,
  className,
}: DropZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateFile = useCallback(
    (file: File): string | null => {
      // Check file type
      if (!ACCEPTED_AUDIO_TYPES.includes(file.type)) {
        const ext = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!ACCEPTED_EXTENSIONS.includes(ext)) {
          return `Invalid file type. Accepted: ${ACCEPTED_EXTENSIONS.join(', ')}`;
        }
      }

      // Check file size
      if (file.size > maxSize) {
        const maxMB = Math.round(maxSize / (1024 * 1024));
        return `File too large. Maximum size: ${maxMB}MB`;
      }

      return null;
    },
    [maxSize]
  );

  const handleFile = useCallback(
    (file: File) => {
      setError(null);
      const validationError = validateFile(file);

      if (validationError) {
        setError(validationError);
        return;
      }

      onFileSelect(file);
    },
    [validateFile, onFileSelect]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragging(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (disabled) return;

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFile(files[0]);
      }
    },
    [disabled, handleFile]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFile(files[0]);
      }
      // Reset input so same file can be selected again
      e.target.value = '';
    },
    [handleFile]
  );

  return (
    <div className={className}>
      <label
        className={clsx(
          'flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-xl cursor-pointer transition-colors',
          {
            'border-zinc-700 bg-zinc-900/50 hover:border-zinc-600 hover:bg-zinc-900': !isDragging && !disabled,
            'border-jelly-500 bg-jelly-500/10': isDragging,
            'border-zinc-800 bg-zinc-900/30 cursor-not-allowed opacity-50': disabled,
          }
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center justify-center pt-5 pb-6">
          <div
            className={clsx('w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-colors', {
              'bg-zinc-800': !isDragging,
              'bg-jelly-500/20': isDragging,
            })}
          >
            {isDragging ? (
              <Upload className="w-6 h-6 text-jelly-400" />
            ) : (
              <Music className="w-6 h-6 text-zinc-400" />
            )}
          </div>
          <p className="mb-2 text-sm text-zinc-300">
            <span className="font-semibold">Click to upload</span> or drag and drop
          </p>
          <p className="text-xs text-zinc-500">MP3, WAV, M4A, OGG, FLAC (max {Math.round(maxSize / (1024 * 1024))}MB)</p>
        </div>
        <input
          type="file"
          className="hidden"
          accept={accept}
          onChange={handleInputChange}
          disabled={disabled}
        />
      </label>

      {error && (
        <div className="mt-3 flex items-center gap-2 text-red-400 text-sm">
          <X className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
